/* eslint-disable max-lines */
import escapeRegExp from 'lodash/escapeRegExp'
import trim from 'lodash/trim'
import { ProcessPublishStatus } from 'components/Pages/MessagesPage/SupportMessages/components/SupportThreadFilter'
import { SupportMessageThreadStatus } from 'interfaces/message'
import { IUserWithRelations } from 'interfaces/user'
import { ESupportThreadSortBy } from 'pages/SupportInboxPage/constants'

function getSupportThreadSort(sortBy?: ESupportThreadSortBy) {
  if (sortBy === ESupportThreadSortBy.UNCLAIMED_FIRST) {
    return { status: 1, lastMessageAt: -1 }
  }
  if (sortBy === ESupportThreadSortBy.NAME) {
    return { 'step.name': 1 }
  }
  if (sortBy === ESupportThreadSortBy.NEWEST) {
    return { lastMessageAt: -1 }
  }
  if (sortBy === ESupportThreadSortBy.UPDATED) {
    return { updatedAt: -1 }
  }
  return { lastMessageAt: -1 }
}

export function getSupportMessageThread(
  organizationId: number = 0,
  sortBy: ESupportThreadSortBy | undefined,
  status: SupportMessageThreadStatus[] | undefined,
  publishStatus: ProcessPublishStatus | undefined,
  keyword: string,
  limit: number,
  userId: number,
  isBasicUser: boolean,
  isUnclaimed: boolean,
  isClaimedByMe: boolean,
  isClaimedByOthers: boolean,
  isResolved: boolean,
  participants: IUserWithRelations[],
  isCount?: boolean
) {
  let pipeline: any[] = [
    {
      $match: {
        organizationId: organizationId
      }
    },
    {
      $lookup: {
        from: 'SupportMessage',
        localField: '_id',
        foreignField: 'supportThreadId',
        as: 'supportMessages'
      }
    },
    {
      $unwind: {
        path: '$supportMessages'
      }
    },
    {
      $sort: {
        'supportMessages.createdAt': -1
      }
    },
    {
      $lookup: {
        from: 'User',
        localField: 'supportMessages.userId',
        foreignField: '_id',
        as: 'supportMessages.user'
      }
    },
    {
      $unwind: {
        path: '$supportMessages.user'
      }
    },
    {
      $group: {
        _id: '$_id',
        id: {
          $first: '$_id'
        },
        createdAt: {
          $first: '$createdAt'
        },
        updatedAt: {
          $first: '$updatedAt'
        },
        lastMessageAt: {
          $first: '$lastMessageAt'
        },
        name: {
          $first: '$name'
        },
        subject: {
          $first: '$subject'
        },
        processId: {
          $first: '$processId'
        },
        status: {
          $first: '$status'
        },
        organizationId: {
          $first: '$organizationId'
        },
        stepId: {
          $first: '$stepId'
        },
        supportMessage: {
          $first: '$supportMessages'
        },
        supportMessages: {
          $push: '$supportMessages'
        },
        claimedBy: {
          $first: '$claimedBy'
        }
      }
    },
    {
      $lookup: {
        from: 'Step',
        localField: 'stepId',
        foreignField: '_id',
        pipeline: [
          {
            $lookup: {
              from: 'Step',
              localField: 'originalStepId',
              foreignField: '_id',
              as: 'originalSteps'
            }
          },
          {
            $addFields: {
              originalStep: { $arrayElemAt: ['$originalSteps', 0] }
            }
          }
        ],
        as: 'step'
      }
    },
    {
      $unwind: {
        path: '$step',
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $sort: getSupportThreadSort(sortBy)
    },
    {
      $lookup: {
        from: 'Process',
        localField: 'step.processId',
        foreignField: '_id',
        as: 'step.process'
      }
    },
    {
      $unwind: {
        path: '$step.process',
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $lookup: {
        from: 'Process',
        localField: 'processId',
        foreignField: '_id',
        as: 'process',
        pipeline: [
          {
            $lookup: {
              from: 'DocumentType',
              localField: 'documentTypeId',
              foreignField: '_id',
              as: 'documentType',
              pipeline: [
                {
                  $lookup: {
                    from: 'Icon',
                    localField: 'iconId',
                    foreignField: '_id',
                    as: 'iconBuilder'
                  }
                },
                {
                  $unwind: {
                    path: '$iconBuilder',
                    preserveNullAndEmptyArrays: true
                  }
                }
              ]
            }
          },
          {
            $unwind: {
              path: '$documentType',
              preserveNullAndEmptyArrays: true
            }
          }
        ]
      }
    },
    {
      $unwind: {
        path: '$process',
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $lookup: {
        from: 'SupportMessageThreadUserSeen',
        let: {
          supportThreadId: '$_id'
        },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  {
                    $eq: ['$supportThreadId', '$$supportThreadId']
                  },
                  {
                    $eq: ['$userId', userId]
                  }
                ]
              }
            }
          }
        ],
        as: 'supportMessageThreadUserSeens'
      }
    },
    {
      $lookup: {
        from: 'SupportMessageThreadUser',
        localField: '_id',
        foreignField: 'supportThreadId',
        as: 'supportMessageThreadUsers'
      }
    },
    {
      $lookup: {
        from: 'User',
        localField: 'supportMessageThreadUsers.userId',
        foreignField: '_id',
        as: 'users'
      }
    },
    {
      $match: {
        $or: [
          {
            'step.name': {
              $regex: `.*${escapeRegExp(trim(keyword))}.*`,
              $options: 'i'
            }
          },
          {
            'process.name': {
              $regex: `.*${escapeRegExp(trim(keyword))}.*`,
              $options: 'i'
            }
          },
          {
            subject: {
              $regex: `.*${escapeRegExp(trim(keyword))}.*`,
              $options: 'i'
            }
          }
        ]
      }
    }
  ]

  if (isBasicUser) {
    pipeline = [
      ...pipeline,
      {
        $addFields: {
          userIds: '$supportMessages.userId'
        }
      },
      {
        $match: {
          $or: [
            {
              userIds: {
                $elemMatch: {
                  $eq: userId
                }
              }
            },
            {
              'step.process.userId': userId
            }
          ]
        }
      },
      {
        $unset: 'userIds'
      }
    ]
  }

  if (isUnclaimed || isClaimedByMe || isClaimedByOthers || isResolved) {
    pipeline[0].$match.$or = []
    if (isUnclaimed) {
      pipeline[0].$match.$or.push({
        status: SupportMessageThreadStatus.UNCLAIMED
      })
    }
    if (isResolved) {
      pipeline[0].$match.$or.push({
        status: SupportMessageThreadStatus.RESOLVED
      })
    }
    if (isClaimedByMe) {
      pipeline[0].$match.$or.push({
        claimedBy: userId
      })
    }
    if (isClaimedByOthers) {
      pipeline[0].$match.$or.push({
        claimedBy: {
          $ne: userId,
          $gt: 0
        }
      })
    }
  }

  if (participants?.length > 0) {
    const participantIds = participants.map(participant => participant?.id)
    pipeline = [
      ...pipeline,
      {
        $match: {
          'supportMessages.userId': {
            $in: participantIds
          }
        }
      }
    ]
  }

  if (!isCount) {
    pipeline = [
      ...pipeline,
      {
        $limit: limit
      }
    ]
  }

  if (publishStatus) {
    const booleanValue = publishStatus === ProcessPublishStatus.DRAFT ? false : true
    pipeline[15] = {
      $match: {
        'step.name': {
          $regex: `.*${escapeRegExp(trim(keyword))}.*`,
          $options: 'i'
        },
        'step.process.isPublished': booleanValue
      }
    }
  }

  return pipeline
}

export function getGeneralMessageThreadForUnreadCount(
  organizationId: number = 0,
  userId: number,
  isBasicUser: boolean
) {
  let pipeline: any[] = [
    {
      $match: {
        organizationId: organizationId
      }
    },
    {
      $lookup: {
        from: 'GroupMessage',
        localField: '_id',
        foreignField: 'groupThreadId',
        as: 'groupMessages'
      }
    },
    {
      $unwind: {
        path: '$groupMessages'
      }
    },
    {
      $lookup: {
        from: 'User',
        localField: 'groupMessages.userId',
        foreignField: '_id',
        as: 'groupMessages.user'
      }
    },
    {
      $unwind: {
        path: '$groupMessages.user'
      }
    },
    {
      $group: {
        _id: '$_id',
        id: { $first: '$_id' },
        createdAt: { $first: '$createdAt' },
        updatedAt: { $first: '$updatedAt' },
        lastMessageAt: { $first: '$lastMessageAt' },
        name: { $first: '$name' },
        organizationId: { $first: '$organizationId' },
        groupMessages: { $push: '$groupMessages' }
      }
    },
    {
      $lookup: {
        from: 'GroupMessageThreadUserSeen',
        let: { groupThreadId: '$_id' },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ['$groupThreadId', '$$groupThreadId'] },
                  { $eq: ['$userId', userId] }
                ]
              }
            }
          }
        ],
        as: 'groupMessageThreadUserSeens'
      }
    }
  ]

  if (isBasicUser) {
    pipeline = [
      ...pipeline,
      {
        $addFields: {
          userIds: '$groupMessages.userId'
        }
      },
      {
        $match: {
          userIds: {
            $elemMatch: {
              $eq: userId
            }
          }
        }
      },
      {
        $unset: 'userIds'
      }
    ]
  }

  return pipeline
}

export function getSupportMessageThreadForUnreadCount(
  organizationId: number = 0,
  userId: number,
  isBasicUser: boolean
) {
  let pipeline: any[] = [
    {
      $match: {
        organizationId: organizationId
      }
    },
    {
      $lookup: {
        from: 'SupportMessage',
        localField: '_id',
        foreignField: 'supportThreadId',
        as: 'supportMessages'
      }
    },
    {
      $unwind: {
        path: '$supportMessages'
      }
    },
    {
      $lookup: {
        from: 'User',
        localField: 'supportMessages.userId',
        foreignField: '_id',
        as: 'supportMessages.user'
      }
    },
    {
      $unwind: {
        path: '$supportMessages.user'
      }
    },
    {
      $group: {
        _id: '$_id',
        id: { $first: '$_id' },
        createdAt: { $first: '$createdAt' },
        updatedAt: { $first: '$updatedAt' },
        lastMessageAt: { $first: '$lastMessageAt' },
        name: { $first: '$name' },
        subject: { $first: '$subject' },
        processId: { $first: '$processId' },
        status: { $first: '$status' },
        organizationId: { $first: '$organizationId' },
        stepId: { $first: '$stepId' },
        supportMessage: { $first: '$supportMessages' },
        supportMessages: { $push: '$supportMessages' },
        claimedBy: { $first: '$claimedBy' }
      }
    },
    {
      $lookup: {
        from: 'SupportMessageThreadUserSeen',
        let: { supportThreadId: '$_id' },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ['$supportThreadId', '$$supportThreadId'] },
                  { $eq: ['$userId', userId] }
                ]
              }
            }
          }
        ],
        as: 'supportMessageThreadUserSeens'
      }
    }
  ]

  if (isBasicUser) {
    pipeline = [
      ...pipeline,
      {
        $addFields: {
          userIds: '$supportMessages.userId'
        }
      },
      {
        $match: {
          $or: [
            {
              userIds: {
                $elemMatch: {
                  $eq: userId
                }
              }
            },
            {
              'step.process.userId': userId
            }
          ]
        }
      },
      {
        $unset: 'userIds'
      }
    ]
  }

  return pipeline
}
